import { FontLoader } from "./fontLoader";

/**
 * Shared Card Renderer Utility
 * Provides consistent card rendering logic for both web and admin applications
 */

export interface CardData {
  userName: string;
}

export interface CardRendererOptions {
  maxWidth?: number;
  fontFamily?: string;
  textColor?: string;
  fontSize?: number;
  enableAdaptiveSizing?: boolean;
}

const DEFAULT_OPTIONS: CardRendererOptions = {
  maxWidth: 600,
  fontFamily: FontLoader.getFontFamily("Gobold"),
  textColor: "#ffffff",
  fontSize: 24,
  enableAdaptiveSizing: true,
};

export class CardRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private options: Required<CardRendererOptions>;
  private devicePixelRatio: number;

  constructor(options: CardRendererOptions = {}) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    } as Required<CardRendererOptions>;
    this.devicePixelRatio = window.devicePixelRatio || 1;
    this.canvas = document.createElement("canvas");

    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Unable to get 2D context from canvas");
    }
    this.ctx = ctx;
  }

  /**
   * Calculate optimal font size that fits within the specified width with margins
   */
  private calculateOptimalFontSize(text: string, maxWidth: number): number {
    if (!this.options.enableAdaptiveSizing) {
      // Return scaled font size without adaptive sizing
      return (this.options.fontSize * this.options.maxWidth) / 600;
    }

    // Scale font sizes proportionally to display width (not canvas width due to devicePixelRatio)
    const scale = this.options.maxWidth / 600;

    // Proportionally scaled sizes
    const minFontSize = 16 * scale; // Will be ~16px at 600px width
    const maxFontSize = 48 * scale; // Will be ~48px at 600px width

    // Target width is 80% of available width to leave lateral margins
    const targetWidth = maxWidth * 0.8;

    // Start with max font size and work down
    let fontSize = maxFontSize;

    // Save current font
    const savedFont = this.ctx.font;

    while (fontSize >= minFontSize) {
      this.ctx.font = `bold ${fontSize}px ${this.options.fontFamily}`;
      const textWidth = this.ctx.measureText(text).width;

      if (textWidth <= targetWidth) {
        // Restore saved font
        this.ctx.font = savedFont;
        return fontSize;
      }

      // Decrease font size by 1px increments for precision
      fontSize -= 1;
    }

    // Restore saved font
    this.ctx.font = savedFont;

    // If we can't fit even at minimum size, return minimum
    return minFontSize;
  }

  /**
   * Draw the background image on canvas
   */
  private async drawBackground(imageUrl: string): Promise<{ displayWidth: number; displayHeight: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        // Calculate canvas dimensions based on image aspect ratio
        const aspectRatio = img.naturalHeight / img.naturalWidth;

        // Set display size
        const displayWidth = this.options.maxWidth;
        const displayHeight = this.options.maxWidth * aspectRatio;

        // Set actual canvas size with device pixel ratio
        this.canvas.width = displayWidth * this.devicePixelRatio;
        this.canvas.height = displayHeight * this.devicePixelRatio;

        // Set display size via CSS (needed for proper scaling)
        this.canvas.style.width = displayWidth + 'px';
        this.canvas.style.height = displayHeight + 'px';

        // Scale the context to match device pixel ratio
        this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);

        // Draw the image to fill the entire canvas
        this.ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
        resolve({ displayWidth, displayHeight });
      };

      img.onerror = () => {
        // Fallback: use a default aspect ratio with solid color
        const displayWidth = this.options.maxWidth;
        const displayHeight = this.options.maxWidth * 1.5; // Default portrait ratio

        this.canvas.width = displayWidth * this.devicePixelRatio;
        this.canvas.height = displayHeight * this.devicePixelRatio;

        // Set display size via CSS (needed for proper scaling)
        this.canvas.style.width = displayWidth + 'px';
        this.canvas.style.height = displayHeight + 'px';

        this.ctx.scale(this.devicePixelRatio, this.devicePixelRatio);

        this.ctx.fillStyle = "#6a5acd";
        this.ctx.fillRect(0, 0, displayWidth, displayHeight);
        resolve({ displayWidth, displayHeight });
      };

      img.src = imageUrl;
    });
  }

  /**
   * Draw user name on the card
   */
  private drawUserName(data: CardData, displayWidth: number, displayHeight: number): void {
    // Use integer values to avoid sub-pixel blurriness
    const centerX = Math.round(displayWidth / 2);

    // Divide image in half vertically, put name in center of bottom half
    const topHalf = displayHeight / 2;
    const bottomHalfCenter = Math.round(topHalf + topHalf / 2);

    // Save context state before text rendering
    this.ctx.save();

    // Disable image smoothing for text (sharper text)
    this.ctx.imageSmoothingEnabled = false;

    this.ctx.fillStyle = this.options.textColor;
    this.ctx.textAlign = "center";
    this.ctx.textBaseline = "middle";

    // Calculate optimal font size
    const availableWidth = displayWidth;
    const optimalFontSize = this.calculateOptimalFontSize(
      data.userName,
      availableWidth,
    );

    // Round font size to avoid fractional pixels
    const roundedFontSize = Math.round(optimalFontSize);
    this.ctx.font = `bold ${roundedFontSize}px ${this.options.fontFamily}`;

    // Use shadow for text depth and readability
    const shadowScale = displayWidth / 600;
    this.ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
    this.ctx.shadowBlur = Math.round(3 * shadowScale); // More blur for depth
    this.ctx.shadowOffsetX = Math.round(2 * shadowScale);
    this.ctx.shadowOffsetY = Math.round(2 * shadowScale);

    this.ctx.fillText(data.userName, centerX, bottomHalfCenter);

    // Restore context state
    this.ctx.restore();
  }

  /**
   * Draw rounded corners on the card
   */
  private drawRoundedCorners(displayWidth: number, displayHeight: number): void {
    const radius = (15 * displayWidth) / 600;

    this.ctx.globalCompositeOperation = "destination-in";
    this.ctx.beginPath();
    this.ctx.roundRect(0, 0, displayWidth, displayHeight, radius);
    this.ctx.fill();
    this.ctx.globalCompositeOperation = "source-over";
  }

  /**
   * Generate a card with the given data and background image
   */
  public async generateCard(
    data: CardData,
    backgroundImageUrl: string,
  ): Promise<HTMLCanvasElement> {
    // Ensure Gobold font is loaded before rendering
    await FontLoader.loadGoboldFont();

    // Draw card elements
    const { displayWidth, displayHeight } = await this.drawBackground(backgroundImageUrl);
    this.drawUserName(data, displayWidth, displayHeight);
    this.drawRoundedCorners(displayWidth, displayHeight);

    return this.canvas;
  }

  /**
   * Generate card as blob for download
   */
  public async generateCardBlob(
    data: CardData,
    backgroundImageUrl: string,
    format: "png" | "jpeg" = "png",
    quality?: number,
  ): Promise<Blob> {
    await this.generateCard(data, backgroundImageUrl);

    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to generate card blob"));
          }
        },
        `image/${format}`,
        quality,
      );
    });
  }
}

// Helper functions for common use cases

/**
 * Generate card for display (smaller, window-based size)
 */
export const generateDisplayCard = async (
  data: CardData,
  backgroundImageUrl: string,
  maxWidth?: number,
): Promise<HTMLCanvasElement> => {
  const displayWidth = maxWidth || Math.min(window.innerWidth - 40, 600);
  const renderer = new CardRenderer({ maxWidth: displayWidth });
  return renderer.generateCard(data, backgroundImageUrl);
};

/**
 * Generate card for export (high resolution)
 */
export const generateExportCard = async (
  data: CardData,
  backgroundImageUrl: string,
): Promise<HTMLCanvasElement> => {
  const renderer = new CardRenderer({ maxWidth: 1200 });
  return renderer.generateCard(data, backgroundImageUrl);
};
